import { cn } from '@/lib/cn';

type LabeledToggleSwitchProps = {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  busy?: boolean;
  offLabel?: string;
  onLabel?: string;
  className?: string;
  id?: string;
  'aria-label'?: string;
};

/**
 * 双标签滑动开关：左侧「关闭」、右侧「开启」，滑块随状态左右移动。
 */
export function LabeledToggleSwitch({
  checked,
  onChange,
  disabled = false,
  busy = false,
  offLabel = '关闭',
  onLabel = '开启',
  className,
  id,
  'aria-label': ariaLabel,
}: LabeledToggleSwitchProps) {
  const inactive = disabled || busy;

  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel ?? (checked ? '已开启，点击关闭' : '已关闭，点击开启')}
      aria-busy={busy}
      disabled={inactive}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-9 w-[6.25rem] shrink-0 select-none rounded-full p-0.5 shadow-inner transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/45 focus-visible:ring-offset-2',
        checked
          ? 'bg-gradient-to-r from-teal-600 to-emerald-600 shadow-teal-900/20'
          : 'bg-slate-200/95 shadow-slate-900/10',
        inactive ? 'cursor-not-allowed opacity-55' : 'cursor-pointer hover:brightness-[1.02] active:scale-[0.98]',
        className,
      )}
    >
      <span className="relative z-10 grid h-full w-full grid-cols-2 items-center text-center text-[11px] font-semibold leading-none tracking-wide">
        <span
          className={cn(
            'px-0.5 transition-colors duration-200',
            !checked ? 'text-slate-800' : 'text-white/70',
          )}
        >
          {offLabel}
        </span>
        <span
          className={cn(
            'px-0.5 transition-colors duration-200',
            checked ? 'text-white' : 'text-slate-500',
          )}
        >
          {onLabel}
        </span>
      </span>

      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute top-0.5 bottom-0.5 left-0.5 w-[calc(50%-4px)] rounded-full bg-white shadow-md ring-1 ring-black/[0.06] transition-transform duration-200 ease-out',
          checked && 'translate-x-[calc(100%+4px)]',
        )}
      />

      {busy ? (
        <span
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-full bg-white/35 backdrop-blur-[1px]"
          aria-hidden
        >
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-400/80 border-t-teal-600" />
        </span>
      ) : null}
    </button>
  );
}
